import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, setHours, setMinutes, isBefore, startOfDay } from 'date-fns';
import { toast } from 'sonner';
import Modal from '../Modal';
import Button from '../Button';
import { useSchedulePost, useReschedulePost } from '../../hooks/useCalendar';
import { listPosts, type PostWithRelations } from '../../lib/api';
import type { ScheduledPostEntry, BestTimeSlot } from '../../types/calendar';

interface SchedulePostModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Success callback after scheduling */
  onSuccess?: () => void;
  /** Mode: 'schedule' for new posts, 'reschedule' for existing */
  mode: 'schedule' | 'reschedule';
  /** Pre-selected date (optional) */
  initialDate?: Date;
  /** Post to reschedule (required for reschedule mode) */
  existingPost?: ScheduledPostEntry;
  /** Pre-selected post ID (for scheduling from ApproveBoard) */
  preSelectedPostId?: string;
  /** Timezone for the workspace */
  timezone?: string;
  /** Best time suggestions to show */
  suggestions?: BestTimeSlot[];
}

/**
 * SchedulePostModal component
 * Modal for scheduling new posts or rescheduling existing posts.
 * Supports post selection, date/time picker, auto-publish toggle,
 * and conflict warning display.
 */
export default function SchedulePostModal({
  isOpen,
  onClose,
  onSuccess,
  mode,
  initialDate,
  existingPost,
  preSelectedPostId,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  suggestions,
}: SchedulePostModalProps) {
  const { t } = useTranslation();

  // State
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [approvedPosts, setApprovedPosts] = useState<PostWithRelations[]>([]);
  const [selectedPostId, setSelectedPostId] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate || new Date());
  const [selectedTime, setSelectedTime] = useState<string>('09:00');
  const [autoPublish, setAutoPublish] = useState(true);
  const [error, setError] = useState<string>('');

  // Mutations
  const { mutate: schedulePost, isPending: isScheduling } = useSchedulePost();
  const { mutate: reschedulePost, isPending: isRescheduling } = useReschedulePost();

  const isSubmitting = isScheduling || isRescheduling;

  // Load approved posts for scheduling mode
  useEffect(() => {
    if (isOpen && mode === 'schedule') {
      loadApprovedPosts();
    }
  }, [isOpen, mode]);

  // Set initial values when modal opens
  useEffect(() => {
    if (isOpen) {
      setError('');

      if (mode === 'reschedule' && existingPost) {
        // Pre-fill with existing schedule
        const existingDate = new Date(existingPost.scheduledAt);
        setSelectedDate(existingDate);
        setSelectedTime(format(existingDate, 'HH:mm'));
        setAutoPublish(existingPost.autoPublish);
        setSelectedPostId(existingPost.postId);
      } else {
        // New scheduling
        setSelectedDate(initialDate || new Date());
        setSelectedTime('09:00');
        setAutoPublish(true);
        setSelectedPostId(preSelectedPostId || '');
      }
    }
  }, [isOpen, mode, existingPost, initialDate, preSelectedPostId]);

  /**
   * Load approved posts that can be scheduled
   */
  const loadApprovedPosts = async () => {
    setIsLoadingPosts(true);
    setError('');

    try {
      const response = await listPosts({
        status: 'approved',
        limit: 50,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });
      setApprovedPosts(response.posts || []);

      // Auto-select if only one post or preSelectedPostId provided
      if (preSelectedPostId) {
        setSelectedPostId(preSelectedPostId);
      } else if (response.posts?.length === 1) {
        setSelectedPostId(response.posts[0].post.id);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('calendar.schedule.errors.loadFailed'));
    } finally {
      setIsLoadingPosts(false);
    }
  };

  /**
   * Handle suggestion selection - fill in date and time
   */
  const handleSuggestionSelect = (suggestion: BestTimeSlot) => {
    // Get next occurrence of this day/time
    const now = new Date();
    const targetDay = suggestion.dayOfWeek;
    const currentDay = now.getDay();

    let daysUntilTarget = targetDay - currentDay;
    if (daysUntilTarget <= 0) {
      daysUntilTarget += 7; // Move to next week
    }

    const targetDate = new Date(now);
    targetDate.setDate(now.getDate() + daysUntilTarget);
    setSelectedDate(targetDate);

    // Set time
    const hours = suggestion.hourOfDay.toString().padStart(2, '0');
    setSelectedTime(`${hours}:00`);
  };

  /**
   * Validate the form
   */
  const validateForm = (): boolean => {
    // Check post selection for schedule mode
    if (mode === 'schedule' && !selectedPostId) {
      setError(t('calendar.schedule.errors.selectPost'));
      return false;
    }

    // Check date is not in the past
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const scheduledDateTime = setMinutes(setHours(selectedDate, hours), minutes);

    if (isBefore(scheduledDateTime, new Date())) {
      setError(t('calendar.schedule.errors.pastDate'));
      return false;
    }

    return true;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = () => {
    if (!validateForm()) return;

    // Build scheduled datetime
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const scheduledAt = setMinutes(setHours(selectedDate, hours), minutes);
    const scheduledAtISO = scheduledAt.toISOString();

    if (mode === 'schedule') {
      schedulePost(
        {
          postId: selectedPostId,
          scheduledAt: scheduledAtISO,
          timezone,
          autoPublish,
        },
        {
          onSuccess: (data) => {
            toast.success(t('calendar.schedule.success'));

            // Show conflict warning if present
            if (data.conflict?.warning) {
              toast.warning(data.conflict.message);
            }

            onSuccess?.();
            onClose();
          },
          onError: (error: any) => {
            const message = error.response?.data?.message || t('calendar.schedule.errors.failed');
            setError(message);
            toast.error(message);
          },
        }
      );
    } else if (mode === 'reschedule' && existingPost) {
      reschedulePost(
        {
          postId: existingPost.postId,
          scheduledAt: scheduledAtISO,
          timezone,
        },
        {
          onSuccess: () => {
            toast.success(t('calendar.reschedule.success'));
            onSuccess?.();
            onClose();
          },
          onError: (error: any) => {
            const message = error.response?.data?.message || t('calendar.reschedule.errors.failed');
            setError(message);
            toast.error(message);
          },
        }
      );
    }
  };

  /**
   * Get title of selected post
   */
  const getSelectedPostTitle = useMemo(() => {
    if (mode === 'reschedule' && existingPost) {
      return existingPost.post.status || t('calendar.schedule.selectedPost');
    }
    const post = approvedPosts.find((p) => p.post.id === selectedPostId);
    return post?.post.title || post?.post.sourceText?.substring(0, 50) || '';
  }, [mode, existingPost, approvedPosts, selectedPostId, t]);

  /**
   * Format date for date input
   */
  const formatDateForInput = (date: Date): string => {
    return format(date, 'yyyy-MM-dd');
  };

  /**
   * Get minimum date (today)
   */
  const minDate = formatDateForInput(startOfDay(new Date()));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        mode === 'schedule'
          ? t('calendar.schedule.title')
          : t('calendar.reschedule.title')
      }
      size="lg"
    >
      <div className="space-y-6">
        {/* Instructions */}
        <p className="text-gray-700 dark:text-gray-300">
          {mode === 'schedule'
            ? t('calendar.schedule.instructions')
            : t('calendar.reschedule.instructions')}
        </p>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-400 p-4">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {isLoadingPosts && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-black dark:border-gray-600 dark:border-t-white"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">
              {t('calendar.schedule.loadingPosts')}
            </p>
          </div>
        )}

        {/* Post Selector (Schedule Mode Only) */}
        {mode === 'schedule' && !isLoadingPosts && (
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {t('calendar.schedule.selectPost')}
            </h3>

            {approvedPosts.length === 0 ? (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 p-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  {t('calendar.schedule.noApprovedPosts')}
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {approvedPosts.map((post) => (
                  <label
                    key={post.post.id}
                    className={`
                      block p-4 border-2 border-black dark:border-gray-700 cursor-pointer transition-colors rounded-lg
                      ${
                        selectedPostId === post.post.id
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                          : 'bg-white dark:bg-gray-950 hover:bg-gray-50 dark:hover:bg-gray-900'
                      }
                    `}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="post"
                        value={post.post.id}
                        checked={selectedPostId === post.post.id}
                        onChange={(e) => setSelectedPostId(e.target.value)}
                        className="mr-3 accent-black dark:accent-white"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 dark:text-white truncate">
                          {post.post.title || post.post.sourceText?.substring(0, 60) || t('calendar.schedule.untitledPost')}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {post.variants.map((variant) => (
                            <span
                              key={variant.id}
                              className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-xs rounded capitalize"
                            >
                              {variant.platform}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reschedule Mode - Show Current Post */}
        {mode === 'reschedule' && existingPost && (
          <div className="bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 p-4 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              {t('calendar.reschedule.currentPost')}
            </p>
            <p className="font-bold text-gray-900 dark:text-white">
              {getSelectedPostTitle}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('calendar.reschedule.currentTime')}: {format(new Date(existingPost.scheduledAt), 'PPp')}
            </p>
          </div>
        )}

        {/* Date & Time Selection */}
        {(!isLoadingPosts && (mode === 'reschedule' || approvedPosts.length > 0)) && (
          <div className="grid grid-cols-2 gap-4">
            {/* Date Picker */}
            <div className="space-y-2">
              <label className="block text-sm font-bold text-gray-900 dark:text-white">
                {t('calendar.schedule.date')}
              </label>
              <input
                type="date"
                value={formatDateForInput(selectedDate)}
                min={minDate}
                onChange={(e) => setSelectedDate(new Date(e.target.value))}
                className="
                  w-full px-4 py-3 border-2 border-black dark:border-gray-600
                  bg-white dark:bg-gray-950
                  text-gray-900 dark:text-white
                  rounded-lg
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                "
              />
            </div>

            {/* Time Picker */}
            <div className="space-y-2">
              <label className="block text-sm font-bold text-gray-900 dark:text-white">
                {t('calendar.schedule.time')}
              </label>
              <input
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="
                  w-full px-4 py-3 border-2 border-black dark:border-gray-600
                  bg-white dark:bg-gray-950
                  text-gray-900 dark:text-white
                  rounded-lg
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                "
              />
            </div>
          </div>
        )}

        {/* Timezone Display */}
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {t('calendar.schedule.timezone')}: <span className="font-medium">{timezone}</span>
        </div>

        {/* Best Time Suggestions */}
        {suggestions && suggestions.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-gray-900 dark:text-white">
              {t('calendar.schedule.suggestedTimes')}
            </h4>
            <div className="flex flex-wrap gap-2">
              {suggestions.slice(0, 4).map((suggestion, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSuggestionSelect(suggestion)}
                  className="
                    px-3 py-2 border-2 border-gray-300 dark:border-gray-600
                    bg-white dark:bg-gray-800
                    text-gray-900 dark:text-white
                    rounded-lg text-sm
                    hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20
                    transition-colors
                    flex items-center gap-2
                  "
                >
                  <span className="font-medium">{suggestion.dayName}</span>
                  <span className="text-gray-500 dark:text-gray-400">{suggestion.timeDisplay}</span>
                  <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                    {suggestion.score}%
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Auto-Publish Toggle (Schedule Mode Only) */}
        {mode === 'schedule' && (
          <div className="flex items-center justify-between p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg">
            <div>
              <p className="font-bold text-gray-900 dark:text-white">
                {t('calendar.schedule.autoPublish')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('calendar.schedule.autoPublishDescription')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setAutoPublish(!autoPublish)}
              className={`
                relative w-14 h-8 rounded-full transition-colors
                ${autoPublish ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}
              `}
              role="switch"
              aria-checked={autoPublish}
            >
              <span
                className={`
                  absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform
                  ${autoPublish ? 'left-7' : 'left-1'}
                `}
              />
            </button>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t-2 border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="
              px-6 py-3 border-2 border-black dark:border-gray-600
              text-gray-900 dark:text-white
              rounded-lg font-medium
              hover:bg-gray-100 dark:hover:bg-gray-800
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors
            "
          >
            {t('common.cancel')}
          </button>
          <Button
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              (mode === 'schedule' && (!selectedPostId || approvedPosts.length === 0))
            }
            isLoading={isSubmitting}
          >
            {mode === 'schedule'
              ? t('calendar.schedule.submit')
              : t('calendar.reschedule.submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
